import React, { useState, useEffect, useMemo } from "react";
import { PageFormLayout, FormSection, FormActions, FieldLabel } from "./ui";
import { useNavigate } from "react-router-dom";
import { api } from "../services";
import { useTranslation } from "react-i18next";
import NativeInput from "./NativeInput";
import CustomDropdown from "./dropdown/CustomDropdown";
import { toast } from "react-toastify";
import { getPhoneLikeNumberError } from "../utils/validators";
import { invalidateSetupGuideCache } from "../hooks/useSetupGuideStatus";

function AddCategory() {
  const { t, i18n } = useTranslation("translation");
  const isArabic = i18n.language === "ar";
  const [categoryData, setCategoryData] = useState({
    nameEn: "",
    nameAr: "",
    storeId: "",
    // index: "",
    // excludeBranches: "",
  });
  // const [branches, setBranches] = useState([]);
  const [stores, setStores] = useState([]); // List of all stores
  const [storeApps, setStoreApps] = useState([]); // [{ storeId, storeName, apps: [...] }]
  // const [selectedBranch, setSelectedBranch] = useState("");
  // const [selectedBranchId, setSelectedBranchId] = useState(null);
  // const [appId, setAppId] = useState(null);
  const [selectedAppIds, setSelectedAppIds] = useState([]); // Selected app IDs
  const [errors, setErrors] = useState({
    nameEn: "",
    nameAr: "",
    storeId: "",
    general: "",
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStores();
  }, []);

  // Fetch all stores
  const fetchStores = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores/my`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStores(response.data.stores || []);
    } catch (error) {
      console.error(
        "Error fetching stores:",
        error.response?.data || error.message
      );
      setErrors((prev) => ({
        ...prev,
        general: `${t("Failed to fetch stores.")}`,
      }));
    }
  };

  // Fetch apps for all stores when stores change
  useEffect(() => {
    if (stores.length > 0) {
      fetchAppsForAllStores(stores);
    }
  }, [stores]);

  // Fetch apps for all stores
  const fetchAppsForAllStores = async (storesList) => {
    try {
      const token = localStorage.getItem("token");
      const allStoreApps = await Promise.all(
        storesList.map(async (store) => {
          const res = await api.get(`/stores/${store._id}/apps`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          return {
            storeId: store._id,
            storeName: store.name,
            apps: res.data.apps || [],
          };
        })
      );
      setStoreApps(allStoreApps);
    } catch (error) {
      console.error(
        "Error fetching apps for stores:",
        error.response?.data || error.message
      );
      setErrors((prev) => ({
        ...prev,
        general: "Failed to fetch apps for stores.",
      }));
    }
  };

  // // Fetch Store ID
  const fetchStoreId = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await api.get(`/stores`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStores(response.data.stores);
    } catch (error) {
      console.error(
        "Error fetching store ID:",
        error.response?.data || error.message
      );
      setErrors((prev) => ({ ...prev, general: "Failed to fetch store ID." }));
    }
  };

  // // Fetch Branches for Selected Store
  // const fetchBranches = async () => { ... };

  // // Handle Branch Selection
  // const handleBranchSelect = (branch) => { ... };

  // Handle App Checkbox Change
  const handleAppCheckboxChange = (appId) => {
    setSelectedAppIds((prev) =>
      prev.includes(appId)
        ? prev.filter((id) => id !== appId)
        : [...prev, appId]
    );
  };

  // Handle Field Input Change
  const handleFieldChange = (field, value) => {
    // Update data first
    setCategoryData((prev) => ({ ...prev, [field]: value }));

    // Inline validation per field; show message under specific input
    if (field === "nameEn") {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          nameEn: t("English name is required."),
        }));
      } else {
        const phoneError = getPhoneLikeNumberError(value, t);
        if (phoneError) {
          setErrors((prev) => ({ ...prev, nameEn: phoneError }));
        } else if (/[^a-zA-Z0-9\s]/.test(value)) {
          setErrors((prev) => ({
            ...prev,
            nameEn: t(
              "English name must only contain English letters and numbers."
            ),
          }));
        } else {
          setErrors((prev) => ({ ...prev, nameEn: "" }));
        }
      }
    }

    if (field === "nameAr") {
      if (!value.trim()) {
        setErrors((prev) => ({
          ...prev,
          nameAr: t("Arabic name is required."),
        }));
      } else {
        const phoneError = getPhoneLikeNumberError(value, t);
        if (phoneError) {
          setErrors((prev) => ({ ...prev, nameAr: phoneError }));
        } else if (/[^\u0600-\u06FF0-9\s]/.test(value)) {
          setErrors((prev) => ({
            ...prev,
            nameAr: t(
              "Arabic name must only contain Arabic letters and numbers."
            ),
          }));
        } else {
          setErrors((prev) => ({ ...prev, nameAr: "" }));
        }
      }
    }

    if (field === "storeId") {
      if (!value) {
        setErrors((prev) => ({ ...prev, storeId: t("Store name is required") }));
      } else {
        setErrors((prev) => ({ ...prev, storeId: "" }));
      }
    }
  };

  // Handle store selection from Toolbar dropdown
  const handleStoreSelect = (store) => {
    const selectedId = store?._id || "";
    setCategoryData((prev) => ({ ...prev, storeId: selectedId }));

    if (!selectedId) {
      setErrors((prev) => ({ ...prev, storeId: t("Store name is required") }));
    } else {
      setErrors((prev) => ({ ...prev, storeId: "" }));
    }
  };

  const handleSave = async () => {
    // Build inline error bag
    const newErrors = {};
    if (!categoryData.nameEn.trim())
      newErrors.nameEn = t("English name is required.");
    else {
      const phoneErrorEn = getPhoneLikeNumberError(categoryData.nameEn, t);
      if (phoneErrorEn) newErrors.nameEn = phoneErrorEn;
      else if (/[^a-zA-Z0-9\s]/.test(categoryData.nameEn))
        newErrors.nameEn = t(
          "English name must only contain English letters and numbers."
        );
    }

    if (!categoryData.nameAr.trim())
      newErrors.nameAr = t("Arabic name is required.");
    else {
      const phoneErrorAr = getPhoneLikeNumberError(categoryData.nameAr, t);
      if (phoneErrorAr) newErrors.nameAr = phoneErrorAr;
      else if (/[^\u0600-\u06FF0-9\s]/.test(categoryData.nameAr))
        newErrors.nameAr = t(
          "Arabic name must only contain Arabic letters and numbers."
        );
    }

    if (!categoryData.storeId) newErrors.storeId = t("Store name is required");

    setErrors((prev) => ({ ...prev, ...newErrors }));
    if (Object.keys(newErrors).length > 0) return;

    // Only required fields
    const payload = {
      nameAr: categoryData.nameAr,
      nameEn: categoryData.nameEn,
      storeId: categoryData.storeId,
    };
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const response = await api.post(`/category`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(t("Category created successfully"));
      invalidateSetupGuideCache();
      setTimeout(() => navigate("/category-overview"), 1500);
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        general: error.response?.data?.message || t("Failed to add category."),
      }));
      toast.error(error.response?.data?.message || t("Failed to add category."));
    } finally {
      setLoading(false);
    }
  };

  const selectedStoreOption = useMemo(
    () => stores.find((store) => store._id === categoryData.storeId) || null,
    [stores, categoryData.storeId]
  );

  return (
    <PageFormLayout rootClass="add-category" title={t("addcategory")}>
      {errors.general && (
        <div className="alert alert-danger oh-form-alert" role="alert">
          {errors.general}
        </div>
      )}

      <FormSection title={t("categorydetails")}>
        <div className="oh-form-grid">
          <div className={`oh-field${errors.storeId ? " oh-field--invalid" : ""}`}>
            <FieldLabel required tooltip={t("formTooltips.categoryStore")}>
              {t("store")}
            </FieldLabel>
            <CustomDropdown
              options={stores}
              showLable={false}
              invalid={!!errors.storeId}
              selectedOption={selectedStoreOption}
              handleSelect={handleStoreSelect}
              placeholder={t("Select store")}
              backgroundColor="#fff"
            />
            <div className="oh-field__error invalid-feedback">
              {errors.storeId || ""}
            </div>
          </div>
          <NativeInput
            label={t("categorynameenglish")}
            required
            tooltip={t("formTooltips.categoryNameEn")}
            type="text"
            value={categoryData.nameEn}
            onChange={(e) => handleFieldChange("nameEn", e.target.value)}
            invalid={!!errors.nameEn}
            feedback={errors.nameEn}
          />
          <NativeInput
            label={t("categorynamearabic")}
            required
            tooltip={t("formTooltips.categoryNameAr")}
            type="text"
            value={categoryData.nameAr}
            onChange={(e) => handleFieldChange("nameAr", e.target.value)}
            invalid={!!errors.nameAr}
            feedback={errors.nameAr}
          />
        </div>
      </FormSection>

      <FormActions
        isRtl={isArabic}
        cancelLabel={t("cancel")}
        saveLabel={t("save")}
        savingLabel={t("saving")}
        onCancel={() => navigate("/category-overview")}
        onSave={handleSave}
        saving={loading}
      />
    </PageFormLayout>
  );
}

export default AddCategory;
